import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Edit, Trash2, Download, FileMinus, Eye, BarChart2, FileText } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Project } from "@/types/project-types";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [newProject, setNewProject] = useState({
    name: "",
    description: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('projects').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: "Error",
        description: "Failed to load projects. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createProject = async () => {
    if (!newProject.name.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive"
      });
      return;
    }
    setIsCreating(true);
    try {
      const {
        data,
        error
      } = await supabase.from('projects').insert({
        name: newProject.name,
        description: newProject.description || null
      }).select();
      if (error) throw error;
      if (data && data[0]) {
        navigate(`/project/${data[0].id}`);
        toast({
          title: "Success",
          description: "Project created successfully"
        });
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast({
        title: "Error",
        description: "Failed to create project. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
      setNewProject({
        name: "",
        description: ""
      });
      setIsCreating(false);
    }
  };

  const deleteProject = async (id: string) => {
    if (!confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
      return;
    }
    try {
      const {
        error
      } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
      setProjects(projects.filter(project => project.id !== id));
      toast({
        title: "Success",
        description: "Project deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project. Please try again.",
        variant: "destructive"
      });
    }
  };

  const downloadProjectPDF = async (project: Project) => {
    navigate(`/project/${project.id}?action=download`);
  };

  return (
    <div className="container max-w-5xl py-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Projects</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/")} className="border-blue-200 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-700">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            
          </div>
        </div>
        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-700 dark:from-gray-300 dark:to-gray-400 bg-clip-text text-transparent">
          Project Estimates
        </h1>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-blue-100 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-blue-700 dark:text-gray-300">Create New Project</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="project-name" className="block text-sm font-medium mb-1">Project Name</label>
              <Input id="project-name" placeholder="Enter project name" value={newProject.name} onChange={e => setNewProject({
              ...newProject,
              name: e.target.value
            })} />
            </div>
            <div>
              <label htmlFor="project-description" className="block text-sm font-medium mb-1">Description (optional)</label>
              <Textarea id="project-description" placeholder="Enter project description" value={newProject.description} onChange={e => setNewProject({
              ...newProject,
              description: e.target.value
            })} />
            </div>
            <Button onClick={createProject} disabled={isCreating} className="w-full bg-blue-600 hover:bg-blue-700">
              {isCreating ? "Creating..." : "Create Project"} <Plus className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-gray-900 border-blue-100 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-blue-700 dark:text-gray-300">My Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div> : projects.length === 0 ? <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No projects yet. Create your first project to get started.
            </div> : <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-50 dark:bg-gray-800">
                    <TableHead>Project Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(project => <TableRow key={project.id}>
                      <TableCell className="font-medium">{project.name}</TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                        {project.description || "No description"}
                      </TableCell>
                      <TableCell className="text-sm">v{project.version}</TableCell>
                      <TableCell className="text-sm">{new Date(project.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => navigate(`/project/${project.id}`)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => navigate(`/project/${project.id}`)} title="Update">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => downloadProjectPDF(project)} title="Download PDF">
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-600" onClick={() => navigate(`/project/${project.id}?tab=analytics`)} title="View Analytics">
                            <BarChart2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => deleteProject(project.id)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>}
        </CardContent>
      </Card>

      <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
        © {new Date().getFullYear()} <a href="https://cardyai.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">cardyai.com</a>. All rights reserved. | v1.2.0
      </div>
    </div>
  );
};

export default Projects;
